import axios from 'axios';

import {
  LeadCampaignAudience,
  LeadCampaignAudiencePreview,
  LeadCampaignGeneratePayload,
  LeadCampaignGenerateResult,
  LeadCampaignListResponse,
  LeadCampaignRecipientListResponse,
  LeadCampaignSchema,
  LeadBrandCommunityOption,
  LeadCreatePayload,
  LeadEmailBrandAssets,
  LeadEmailReplyListResponse,
  LeadEmailReplySchema,
  LeadGroupSchema,
  LeadImportPreview,
  LeadImportResult,
  LeadListResponse,
  LeadMetaSchema,
  LeadSchema,
  LeadStatsSchema,
} from '../data/types';

const secretKey = process.env.REACT_APP_SECRET_KEY ?? '';
const apiKey = process.env.REACT_APP_API_KEY ?? '';
/** Leads CRM always targets the production API regardless of REACT_APP_NODE_ENV. */
const leadsApiUrl = (process.env.REACT_APP_API_URL_PROD || 'https://api.homecookapp.com').replace(/\/$/, '');

const leadsClient = axios.create({
  baseURL: leadsApiUrl,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
    'HOMECOOK-SECRET-KEY': secretKey,
    'HOMECOOK-API-KEY': apiKey,
  },
});

const leadsAdminHeaders = (adminEmail?: string) => ({
  'Leads-Admin-Email': adminEmail ?? '',
});

/** Verify the signed-in staff email is on the leads allowlist. */
export const checkLeadsAdminAccess = async (
  adminEmail?: string
): Promise<{ allowed: boolean; email?: string } | null> => {
  const response = await leadsClient.get('/admin/leads/access', {
    headers: leadsAdminHeaders(adminEmail),
  });
  const data = response.data?.body ?? response.data;
  return data ?? null;
};

/** Fetch CRM metadata (labels, colors, enums). */
export const fetchLeadsMeta = async (adminEmail?: string): Promise<LeadMetaSchema | null> => {
  const response = await leadsClient.get('/admin/leads/meta', {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Fetch aggregate stats for dashboard summary cards (respects list filters). */
export const fetchLeadStats = async (
  adminEmail?: string,
  params: Omit<LeadListParams, 'adminEmail' | 'page' | 'limit' | 'sort' | 'dir'> = {}
): Promise<LeadStatsSchema | null> => {
  const { include_test, test_leads, ...query } = params;
  const response = await leadsClient.get('/admin/leads/stats', {
    headers: leadsAdminHeaders(adminEmail),
    params: {
      ...query,
      ...(test_leads ? { test_leads } : {}),
      ...(include_test ? { include_test: true } : {}),
    },
  });
  return response.data?.body ?? response.data ?? null;
};

export type LeadListParams = {
  adminEmail?: string;
  q?: string;
  lead_category?: string;
  lead_stage?: string;
  lead_status?: string;
  city?: string;
  county?: string;
  state?: string;
  business_zip_code?: string;
  email_domain?: string;
  signup_source?: string;
  resource_type?: string;
  pending_email?: 'true' | 'false';
  include_test?: boolean;
  test_leads?: 'include' | 'only' | 'exclude';
  group_id?: string;
  page?: number;
  limit?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
};

/** List leads with optional filters and pagination. */
export const fetchLeads = async (params: LeadListParams): Promise<LeadListResponse | null> => {
  const { adminEmail, include_test, test_leads, ...query } = params;
  const response = await leadsClient.get('/admin/leads', {
    headers: leadsAdminHeaders(adminEmail),
    params: {
      ...query,
      ...(test_leads ? { test_leads } : {}),
      ...(include_test ? { include_test: true } : {}),
    },
  });
  return response.data?.body ?? response.data ?? null;
};

/** Manually create a lead from the CRM dashboard. */
export const createLead = async (
  payload: LeadCreatePayload,
  adminEmail?: string
): Promise<{ lead?: LeadSchema; action?: string } | null> => {
  const response = await leadsClient.post('/admin/leads', payload, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Preview a CSV/XLSX import without persisting rows. */
export const previewLeadImport = async (
  file: File,
  adminEmail?: string
): Promise<LeadImportPreview | null> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await leadsClient.post('/admin/leads/import/preview', formData, {
    headers: {
      ...leadsAdminHeaders(adminEmail),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data?.body ?? response.data ?? null;
};

/** Import leads from CSV/XLSX. */
export const importLeads = async (
  file: File,
  adminEmail?: string
): Promise<LeadImportResult | null> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await leadsClient.post('/admin/leads/import', formData, {
    headers: {
      ...leadsAdminHeaders(adminEmail),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data?.body ?? response.data ?? null;
};

/** Update CRM fields on a lead. */
export const updateLead = async (
  leadId: string,
  patch: Partial<LeadSchema>,
  adminEmail?: string
): Promise<LeadSchema | null> => {
  const response = await leadsClient.patch(`/admin/leads/${leadId}`, patch, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Send a one-to-one email to a lead. */
export const emailLead = async (
  payload: {
    lead_id: string;
    subject: string;
    message: string;
    use_homecook_template?: boolean;
    brand_assets?: LeadEmailBrandAssets;
  },
  adminEmail?: string
): Promise<{ ok: boolean; simulated?: boolean } | null> => {
  const response = await leadsClient.post('/admin/leads/email', payload, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Send a newsletter / mass email to ids, a group, or a filter. */
export const sendLeadNewsletter = async (
  payload: {
    subject: string;
    message: string;
    lead_ids?: string[];
    group_id?: string;
    filter?: Record<string, unknown>;
    list_filter?: Record<string, string | number | boolean | undefined>;
    use_homecook_template?: boolean;
  },
  adminEmail?: string
): Promise<{ ok: boolean; sent?: number; simulated?: number } | null> => {
  const response = await leadsClient.post('/admin/leads/newsletter', payload, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** List saved lead groups. */
export const fetchLeadGroups = async (
  adminEmail?: string
): Promise<{ items: LeadGroupSchema[] } | null> => {
  const response = await leadsClient.get('/admin/leads/groups', {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Create a named lead group from filter criteria. */
export const createLeadGroup = async (
  payload: {
    name: string;
    description?: string;
    filter?: Record<string, unknown>;
    lead_ids?: string[];
    search_q?: string;
  },
  adminEmail?: string
): Promise<LeadGroupSchema | null> => {
  const response = await leadsClient.post('/admin/leads/groups', payload, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Add or remove explicit lead membership on a saved group. */
export const updateLeadGroupMembers = async (
  groupId: string,
  payload: {
    add?: string[];
    remove?: string[];
    name?: string;
    description?: string;
    pinned_to_tab_bar?: boolean;
  },
  adminEmail?: string
): Promise<LeadGroupSchema | null> => {
  const body: Record<string, unknown> = { group_id: groupId };
  if (payload.add?.length) body.add_lead_ids = payload.add;
  if (payload.remove?.length) body.remove_lead_ids = payload.remove;
  if (payload.name !== undefined) body.name = payload.name;
  if (payload.description !== undefined) body.description = payload.description;
  if (payload.pinned_to_tab_bar !== undefined) body.pinned_to_tab_bar = payload.pinned_to_tab_bar;

  const headers = leadsAdminHeaders(adminEmail);
  const parseResult = (response: { data?: { body?: LeadGroupSchema } & LeadGroupSchema }) =>
    response.data?.body ?? response.data ?? null;

  try {
    const response = await leadsClient.post('/admin/leads/groups', body, { headers });
    return parseResult(response);
  } catch {
    const response = await leadsClient.post(`/admin/leads/groups/${groupId}/members`, body, {
      headers,
    });
    return parseResult(response);
  }
};

/** Delete a lead from the CRM. */
export const deleteLead = async (
  leadId: string,
  adminEmail?: string
): Promise<{ ok: boolean; deleted_id?: string } | null> => {
  const response = await leadsClient.delete(`/admin/leads/${leadId}`, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Delete a saved lead group. */
export const deleteLeadGroup = async (
  groupId: string,
  adminEmail?: string
): Promise<{ ok: boolean; deleted_id?: string } | null> => {
  const response = await leadsClient.delete(`/admin/leads/groups/${groupId}`, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** List email campaigns (drafts and sent). */
export const fetchLeadCampaigns = async (
  adminEmail?: string,
  params: { page?: number; limit?: number; status?: string } = {}
): Promise<LeadCampaignListResponse | null> => {
  const response = await leadsClient.get('/admin/leads/campaigns', {
    headers: leadsAdminHeaders(adminEmail),
    params,
  });
  return response.data?.body ?? response.data ?? null;
};

/** Fetch a single campaign (for send progress polling). */
export const fetchLeadCampaign = async (
  campaignId: string,
  adminEmail?: string
): Promise<LeadCampaignSchema | null> => {
  const response = await leadsClient.get(`/admin/leads/campaigns/${campaignId}`, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Create a draft email campaign. */
export const createLeadCampaign = async (
  payload: Partial<LeadCampaignSchema>,
  adminEmail?: string
): Promise<LeadCampaignSchema | null> => {
  const response = await leadsClient.post('/admin/leads/campaigns', payload, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Update a draft campaign. */
export const updateLeadCampaign = async (
  campaignId: string,
  patch: Partial<LeadCampaignSchema>,
  adminEmail?: string
): Promise<LeadCampaignSchema | null> => {
  const response = await leadsClient.patch(`/admin/leads/campaigns/${campaignId}`, patch, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Delete a draft, failed, or test-only campaign. */
export const deleteLeadCampaign = async (
  campaignId: string,
  adminEmail?: string
): Promise<{ ok: boolean; deleted_id?: string } | null> => {
  const response = await leadsClient.delete(`/admin/leads/campaigns/${campaignId}`, {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Send a campaign to its saved audience. */
export const sendLeadCampaign = async (
  campaignId: string,
  adminEmail?: string
): Promise<LeadCampaignSchema | null> => {
  const response = await leadsClient.post(
    `/admin/leads/campaigns/${campaignId}/send`,
    {},
    { headers: leadsAdminHeaders(adminEmail), timeout: 120000 }
  );
  return response.data?.body ?? response.data ?? null;
};

/** Retry failed/skipped recipients for a terminal campaign. */
export const resendLeadCampaign = async (
  campaignId: string,
  adminEmail?: string
): Promise<LeadCampaignSchema | null> => {
  const response = await leadsClient.post(
    `/admin/leads/campaigns/${campaignId}/resend`,
    {},
    { headers: leadsAdminHeaders(adminEmail), timeout: 120000 }
  );
  return response.data?.body ?? response.data ?? null;
};

/** Preview resolved campaign audience size and lead context. */
export const previewLeadCampaignAudience = async (
  audience: LeadCampaignAudience,
  adminEmail?: string
): Promise<LeadCampaignAudiencePreview | null> => {
  const response = await leadsClient.post(
    '/admin/leads/campaigns/audience-preview',
    { audience },
    { headers: leadsAdminHeaders(adminEmail) }
  );
  return response.data?.body ?? response.data ?? null;
};

/** List per-recipient delivery/engagement rows for a sent campaign. */
export const fetchLeadCampaignRecipients = async (
  campaignId: string,
  adminEmail?: string,
  params: { page?: number; limit?: number } = {}
): Promise<LeadCampaignRecipientListResponse | null> => {
  const response = await leadsClient.get(`/admin/leads/campaigns/${campaignId}/recipients`, {
    headers: leadsAdminHeaders(adminEmail),
    params,
  });
  return response.data?.body ?? response.data ?? null;
};

/** Generate newsletter subject and HTML from an admin prompt. */
export const generateLeadCampaign = async (
  payload: LeadCampaignGeneratePayload,
  adminEmail?: string
): Promise<LeadCampaignGenerateResult | null> => {
  const response = await leadsClient.post('/admin/leads/campaigns/generate', payload, {
    headers: leadsAdminHeaders(adminEmail),
    timeout: 90000,
  });
  return response.data?.body ?? response.data ?? null;
};

/** Fetch per-admin brand asset defaults for email composer. */
export const fetchLeadBrandDefaults = async (
  adminEmail?: string
): Promise<{ brand_assets?: LeadEmailBrandAssets } | null> => {
  const response = await leadsClient.get('/admin/leads/brand-defaults', {
    headers: leadsAdminHeaders(adminEmail),
  });
  return response.data?.body ?? response.data ?? null;
};

/** Persist per-admin brand asset defaults. */
export const saveLeadBrandDefaults = async (
  brandAssets: LeadEmailBrandAssets,
  adminEmail?: string
): Promise<{ brand_assets?: LeadEmailBrandAssets } | null> => {
  const response = await leadsClient.put(
    '/admin/leads/brand-defaults',
    { brand_assets: brandAssets },
    { headers: leadsAdminHeaders(adminEmail) }
  );
  return response.data?.body ?? response.data ?? null;
};

/** List communities for brand asset picker (logo + hero photo). */
export const fetchLeadBrandCommunities = async (
  adminEmail?: string
): Promise<LeadBrandCommunityOption[] | null> => {
  const response = await leadsClient.get('/admin/leads/communities', {
    headers: leadsAdminHeaders(adminEmail),
  });
  const data = response.data?.body ?? response.data;
  return data?.items ?? data ?? null;
};

/** Upload an image asset for leads CRM emails (returns public HTTPS URL). */
export const uploadLeadEmailAsset = async (
  file: File,
  adminEmail?: string,
  slot = 'logo'
): Promise<{ url?: string } | null> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('slot', slot);
  const response = await leadsClient.post('/admin/leads/assets', formData, {
    headers: {
      ...leadsAdminHeaders(adminEmail),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data?.body ?? response.data ?? null;
};

/** List recent lead email replies (inbox). */
export const fetchLeadEmailReplies = async (
  adminEmail?: string,
  params: { page?: number; limit?: number; unread?: boolean; lead_id?: string; campaign_id?: string } = {}
): Promise<LeadEmailReplyListResponse | null> => {
  const response = await leadsClient.get('/admin/leads/replies', {
    headers: leadsAdminHeaders(adminEmail),
    params,
  });
  return response.data?.body ?? response.data ?? null;
};

/** Fetch reply thread for a single lead. */
export const fetchLeadRepliesForLead = async (
  leadId: string,
  adminEmail?: string,
  params: { page?: number; limit?: number } = {}
): Promise<LeadEmailReplyListResponse | null> => {
  const response = await leadsClient.get(`/admin/leads/${leadId}/replies`, {
    headers: leadsAdminHeaders(adminEmail),
    params,
  });
  return response.data?.body ?? response.data ?? null;
};

/** Mark a lead email reply as read. */
export const markLeadEmailReplyRead = async (
  replyId: string,
  adminEmail?: string
): Promise<LeadEmailReplySchema | null> => {
  const response = await leadsClient.patch(
    `/admin/leads/replies/${replyId}`,
    { read: true },
    { headers: leadsAdminHeaders(adminEmail) }
  );
  return response.data?.body ?? response.data ?? null;
};
